/**
 * CodableTemplates — katalog startovacích projektů.
 * Design System only (/core/design). Embedded v Library hubu.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  AppWindow,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Database,
  FileCode,
  Filter,
  Globe,
  LayoutTemplate,
  Loader2,
  Monitor,
  ShoppingBag,
} from 'lucide-react';
import { api } from '../../../lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
} from '../../../components/ui';

interface TemplateMeta {
  id: string;
  name: string;
  label: string;
  description: string;
  category: 'blank' | 'website' | 'shop' | 'app' | string;
  backend_default?: 'supabase' | 'none';
  template_url?: string;
  tags?: string[];
}

interface ListResp {
  ok: boolean;
  templates?: TemplateMeta[];
  categories?: Array<{ id: string; label: string }>;
  error?: string;
}

type CatId = '' | 'blank' | 'website' | 'shop' | 'app';

type TFn = TFunction;

/** API vrací CS labely — UI vždy překládá přes locale keys. */
function localizeTemplate(tpl: TemplateMeta, t: TFn): TemplateMeta {
  const idKey = tpl.id.replace(/-/g, '_');
  return {
    ...tpl,
    label: t(`codable.tplLabel_${idKey}`, tpl.label),
    description: t(`codable.tplDesc_${idKey}`, tpl.description),
  };
}

function categoryLabel(category: string, t: TFn): string {
  const map: Record<string, string> = {
    blank: 'codable.tplCatBlank',
    website: 'codable.tplCatWebsite',
    shop: 'codable.tplCatShop',
    app: 'codable.tplCatApp',
  };
  return t(map[category] ?? 'codable.tplCatBlank', category);
}

function categoryVisual(category: string, t: TFn): {
  Icon: typeof Globe;
  iconBg: string;
  ring: string;
  gradient: string;
  badge: 'blue' | 'purple' | 'green' | 'gray' | 'yellow';
  previewLabel: string;
} {
  switch (category) {
    case 'website':
      return {
        Icon: Globe,
        iconBg: 'bg-sky-500/15 text-sky-300',
        ring: 'ring-sky-500/25',
        gradient: 'from-sky-600/40 via-blue-700/30 to-slate-900',
        badge: 'blue',
        previewLabel: t('codable.tplPreviewLanding'),
      };
    case 'shop':
      return {
        Icon: ShoppingBag,
        iconBg: 'bg-amber-500/15 text-amber-300',
        ring: 'ring-amber-500/25',
        gradient: 'from-amber-600/35 via-orange-800/25 to-slate-900',
        badge: 'yellow',
        previewLabel: t('codable.tplPreviewShop'),
      };
    case 'app':
      return {
        Icon: AppWindow,
        iconBg: 'bg-violet-500/15 text-violet-300',
        ring: 'ring-violet-500/25',
        gradient: 'from-violet-600/40 via-indigo-800/30 to-slate-900',
        badge: 'purple',
        previewLabel: t('codable.tplPreviewApp'),
      };
    case 'blank':
    default:
      return {
        Icon: FileCode,
        iconBg: 'bg-emerald-500/15 text-emerald-300',
        ring: 'ring-emerald-500/20',
        gradient: 'from-emerald-700/30 via-slate-800/40 to-slate-900',
        badge: 'green',
        previewLabel: t('codable.tplPreviewBlank'),
      };
  }
}

function getCategoryChips(t: TFunction) {
  return [
    { id: '' as CatId, label: t('codable.libraryAll', 'Vše'), icon: LayoutTemplate, hint: t('codable.templatesHintAll') },
    { id: 'blank' as CatId, label: t('codable.tplCatBlank'), icon: FileCode, hint: t('codable.templatesHintBlank') },
    { id: 'website' as CatId, label: t('codable.tplCatWebsite'), icon: Globe, hint: t('codable.templatesHintWebsite') },
    { id: 'shop' as CatId, label: t('codable.tplCatShop'), icon: ShoppingBag, hint: t('codable.templatesHintShop') },
    { id: 'app' as CatId, label: t('codable.tplCatApp'), icon: AppWindow, hint: t('codable.templatesHintApp') },
  ];
}

/** Katalog šablon — embedded v Library nebo standalone (redirect). */
export function TemplatesCatalog({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [category, setCategory] = useState<CatId>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const chips = useMemo(() => getCategoryChips(t), [t]);

  const q = useQuery({
    queryKey: ['codable-templates'],
    queryFn: () => api.mcp<ListResp>('codable.template_list', {}),
    staleTime: 60_000,
  });

  const allTemplates = useMemo(
    () => (q.data?.templates ?? []).map((tpl) => localizeTemplate(tpl, t)),
    [q.data?.templates, t],
  );
  const templates = useMemo(() => {
    if (!category) return allTemplates;
    return allTemplates.filter((x) => x.category === category);
  }, [allTemplates, category]);

  const selected =
    (selectedId ? templates.find((x) => x.id === selectedId) : null) ??
    templates[0] ??
    null;

  // Keep selection valid when filter changes
  useEffect(() => {
    if (templates.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !templates.some((x) => x.id === selectedId)) {
      setSelectedId(templates[0]!.id);
    }
  }, [templates, selectedId]);

  const catCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const tpl of allTemplates) {
      m.set(tpl.category, (m.get(tpl.category) ?? 0) + 1);
    }
    return m;
  }, [allTemplates]);


  function useTemplate(tpl: TemplateMeta) {
    const params = new URLSearchParams();
    params.set('template', tpl.id);
    if (tpl.backend_default) params.set('backend', tpl.backend_default);
    nav(`/modules/codable/new?${params.toString()}`);
  }

  const body = (
    <div className="space-y-5">
      {/* Info + filters */}
      <Card className="border-white/5 bg-gray-900/50 backdrop-blur-sm shadow-xl shadow-black/20">
        <div className="space-y-4 p-4 sm:p-5">
          {!embedded && (
            <p className="text-sm leading-relaxed text-gray-400">
              {t('codable.templatesVsLibrary')}
            </p>
          )}
          {embedded && (
            <p className="text-sm leading-relaxed text-gray-400">
              {t('codable.templatesDesc')}
            </p>
          )}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <Filter size={11} />
                {t('codable.libraryType')}
              </span>
              {chips.map((k) => {
                const active = category === k.id;
                const Icon = k.icon;
                const count = k.id
                  ? catCounts.get(k.id)
                  : allTemplates.length || undefined;
                return (
                  <Button
                    key={k.id || 'all'}
                    type="button"
                    size="sm"
                    title={k.hint}
                    variant={active ? 'primary' : 'secondary'}
                    icon={<Icon size={13} />}
                    onClick={() => setCategory(k.id)}
                  >
                    {k.label}
                    {typeof count === 'number' && count > 0 && (
                      <Badge variant={active ? 'info' : 'default'} className="ml-1">
                        {count}
                      </Badge>
                    )}
                  </Button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              {q.isFetching ? (
                <>
                  <Loader2 size={12} className="animate-spin text-sky-400" />
                  {t('codable.templatesLoading')}
                </>
              ) : (
                <>
                  <LayoutTemplate size={12} />
                  {t('codable.templatesCount', { n: templates.length })}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {q.isError && (
        <div className="flex items-start gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{(q.error as Error)?.message ?? t('codable.unknownError')}</span>
        </div>
      )}

      {q.data?.ok === false && (
        <div className="flex items-start gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{q.data.error ?? t('codable.unknownError')}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_minmax(300px,360px)] lg:min-h-[52vh]">
        <div className="min-w-0">
          {q.isLoading && (
            <div className="grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-56 animate-pulse rounded-2xl border border-gray-800 bg-gray-900/60"
                />
              ))}
            </div>
          )}

          {!q.isLoading && templates.length === 0 && (
            <Card className="border-white/5 bg-gray-900/40">
              <EmptyState
                className="py-16 px-4"
                icon={<LayoutTemplate size={32} className="mx-auto text-gray-600" />}
                title={t('codable.templatesEmpty')}
                description={t('codable.templatesEmptyDesc')}
              />
            </Card>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {templates.map((tpl) => {
              const vis = categoryVisual(tpl.category, t);
              const Icon = vis.Icon;
              const active = selected?.id === tpl.id;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => setSelectedId(tpl.id)}
                  className={[
                    'group flex flex-col overflow-hidden rounded-2xl border text-left transition-all',
                    active
                      ? `border-sky-500/40 bg-gray-900/80 ring-1 ${vis.ring} shadow-lg shadow-black/30`
                      : 'border-gray-800/90 bg-gray-900/40 hover:border-gray-700 hover:bg-gray-900/70',
                  ].join(' ')}
                >
                  <div className={`relative h-28 overflow-hidden bg-gradient-to-br ${vis.gradient}`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_55%)]" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 ${vis.iconBg}`}
                      >
                        <Icon size={17} />
                      </div>
                      <span className="rounded-md border border-white/10 bg-black/30 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/80 backdrop-blur-sm">
                        {vis.previewLabel}
                      </span>
                    </div>
                    <div className="absolute left-3 top-3 flex gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/15" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/10" />
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col gap-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-white">{tpl.label}</div>
                        <div className="mt-0.5 text-[11px] text-gray-500">
                          id: <span className="font-mono text-gray-400">{tpl.id}</span>
                        </div>
                      </div>
                      <Badge variant={vis.badge}>{categoryLabel(tpl.category, t)}</Badge>
                    </div>
                    <p className="line-clamp-2 text-xs leading-relaxed text-gray-400">
                      {tpl.description}
                    </p>
                    <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
                      <span className="inline-flex items-center gap-1 rounded-md border border-gray-800 bg-gray-950/80 px-1.5 py-0.5 text-[10px] text-gray-400">
                        <Database size={10} />
                        {tpl.backend_default === 'supabase'
                          ? t('codable.templatesBackendCloud')
                          : t('codable.templatesBackendFe')}
                      </span>
                      {tpl.tags?.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="rounded border border-gray-800/80 px-1.5 py-0.5 text-[10px] text-gray-500"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <Card className="flex max-h-[70vh] flex-col border-white/5 bg-gray-900/50 backdrop-blur-sm lg:sticky lg:top-4 lg:max-h-[calc(100vh-12rem)]">
          {!selected ? (
            <EmptyState
              className="my-auto py-14 px-4"
              icon={<Monitor size={28} className="mx-auto text-gray-600" />}
              title={t('codable.templatesPickTitle')}
              description={t('codable.templatesPickDesc')}
            />
          ) : (
            (() => {
              const vis = categoryVisual(selected.category, t);
              const Icon = vis.Icon;
              return (
                <>
                  <div className={`relative h-36 shrink-0 overflow-hidden bg-gradient-to-br ${vis.gradient}`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 ${vis.iconBg}`}
                      >
                        <Icon size={20} />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white">{selected.label}</div>
                        <div className="text-xs text-white/60">{categoryLabel(selected.category, t)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
                    <p className="text-sm leading-relaxed text-gray-300">{selected.description}</p>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                          {t('codable.templatesFieldId')}
                        </div>
                        <div className="mt-1 font-mono text-gray-200">{selected.id}</div>
                      </div>
                      <div className="rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                          {t('codable.backend')}
                        </div>
                        <div className="mt-1 text-gray-200">
                          {selected.backend_default === 'supabase'
                            ? t('codable.templatesBackendCloud')
                            : t('codable.templatesBackendFe')}
                        </div>
                      </div>
                    </div>

                    {selected.tags && selected.tags.length > 0 && (
                      <div>
                        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                          {t('codable.tplTags')}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {selected.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-gray-800 bg-gray-950 px-2.5 py-0.5 text-[11px] text-gray-400"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border border-sky-500/15 bg-sky-500/5 p-3 text-xs leading-relaxed text-sky-100/80">
                      {t('codable.templatesUseHint')}
                    </div>
                  </div>

                  <div className="shrink-0 space-y-2 border-t border-gray-800/80 p-4">
                    <Button
                      variant="primary"
                      className="w-full"
                      icon={<ArrowRight size={15} />}
                      onClick={() => useTemplate(selected)}
                    >
                      {t('codable.templatesUse')}
                    </Button>
                    <Link
                      to={`/modules/codable/new?template=${encodeURIComponent(selected.id)}`}
                      className="block"
                    >
                      <Button variant="ghost" className="w-full" size="sm">
                        {t('codable.templatesOpenWizard')}
                      </Button>
                    </Link>
                  </div>
                </>
              );
            })()
          )}
        </Card>
      </div>

      <p className="text-center text-[11px] text-gray-600">{t('codable.templatesFooter')}</p>
    </div>
  );

  if (embedded) return body;

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        title={t('codable.templatesTitle')}
        description={t('codable.templatesDesc')}
        icon={LayoutTemplate}
        actions={
          <>
            <Link to="/modules/codable">
              <Button variant="ghost" size="sm" icon={<ArrowLeft size={14} />}>
                {t('codable.libraryProjects')}
              </Button>
            </Link>
            <Link to="/modules/codable/library">
              <Button variant="secondary" size="sm" icon={<BookOpen size={14} />}>
                {t('codable.library')}
              </Button>
            </Link>
          </>
        }
      />
      {body}
    </div>
  );
}

/** Standalone route → přesměrování do Library hubu (jedna záložka). */
export default function CodableTemplates() {
  return <Navigate to="/modules/codable/library?tab=templates" replace />;
}
